import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Book, Link as LinkIcon, Link2Off, RefreshCw, CheckCircle2,
         XCircle, Zap, Loader2, ExternalLink, ClipboardPaste,
         ShieldCheck, ClipboardCheck, Send, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const iso = (s) => s ? new Date(s).toLocaleString() : "—";

/**
 * Vinci ⇄ Zoho Books admin panel · connect, status, sync log, backfill.
 */
export function AdminZohoBooksPanel({ api }) {
  const [status, setStatus] = useState(null);
  const [logRows, setLogRows] = useState([]);
  const [logCounts, setLogCounts] = useState({});
  const [logKind, setLogKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [pasteCode, setPasteCode] = useState("");
  const [backfillN, setBackfillN] = useState(20);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/admin/zoho-books/status"),
        api.get(`/admin/zoho-books/sync-log?limit=100&kind=${logKind}`),
      ]);
      setStatus(s); setLogRows(l.rows || []); setLogCounts(l.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Zoho Books status");
    } finally { setLoading(false); }
  }, [api, logKind]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await api.get("/admin/zoho-books/connect");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Zoho connect");
    } finally { setBusy(""); }
  };

  const exchange = async () => {
    setBusy("exchange");
    try {
      await api.post("/admin/zoho-books/exchange-code", { code: pasteCode.trim() });
      toast.success("Zoho Books connected · refresh token stored");
      setPasteCode(""); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code exchange failed");
    } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Books? Future invoices/payouts will stop syncing.")) return;
    setBusy("disconnect");
    try {
      await api.post("/admin/zoho-books/disconnect", {});
      toast.info("Zoho Books disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(""); }
  };

  const backfill = async () => {
    setBusy("backfill");
    try {
      const { data } = await api.post(`/admin/zoho-books/sync/backfill?limit=${backfillN}`, {});
      toast.success(`Backfill · ${data.synced} synced · ${data.failed} failed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally { setBusy(""); }
  };

  if (loading || !status) {
    return <div className="p-10 text-center text-[#8A8A82]"><Loader2 className="animate-spin inline" /></div>;
  }

  return (
    <div data-testid="admin-zoho-books-panel" className="space-y-6">
      <div>
        <div className="overline text-[#B87333] inline-flex items-center gap-1.5">
          <Book size={11} /> Vinci ⇄ Zoho Books · Accounting Sync
        </div>
        <h2 className="font-serif text-3xl md:text-4xl mt-2">Zoho Books Console</h2>
        <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
          Every Vinci invoice, payment and payout auto-books into your Zoho
          Books org for GST filings, reconciliation and audit trail. Sync is
          fire-and-forget — Zoho outages never block Vinci trades.
        </p>
      </div>

      {/* ── Status card ── */}
      <div className={`border rounded-sm p-6 ${status.connected ? "border-[#2FB999]/40 bg-[#02150F]" : "border-[#D8B23E]/40 bg-[#1B1401]/40"}`}
            data-testid="zoho-books-status-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
                  style={{ color: status.connected ? "#2FB999" : "#D8B23E" }}>
              {status.connected
                ? <><CheckCircle2 size={11} /> Connected</>
                : <><XCircle size={11} /> Not connected</>}
            </div>
            <div className="font-serif text-xl text-[#F5F5F0] mt-1">
              Organization <span className="font-mono text-[#D8B23E]">{status.organization_id || "not configured"}</span>
            </div>
            {status.connected && (
              <div className="text-[12px] text-[#8A8A82] font-body mt-1">
                Connected {iso(status.connected_at)} · API {status.api_base}
              </div>
            )}
            {status.vinci_gstin_masked && (
              <div className="mt-3 border border-[#D8B23E]/40 bg-[#1B1401]/40 rounded-sm p-3">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E] inline-flex items-center gap-1.5 mb-2">
                  ✓ PII Masking Active · Board Policy
                </div>
                <div className="text-[11px] font-body text-[#C9C9C2] leading-relaxed">
                  Every payload we push to Zoho Books is scrubbed —
                  Vinci's full GSTIN and PAN are replaced with masked
                  forms before they cross the wire, so no record Zoho
                  stores or generates ever contains the full identifier.
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-mono">
                  <div><span className="text-[#8A8A82]">Vinci GSTIN → Zoho stores</span>{" "}
                    <b className="text-[#2FB999]">{status.vinci_gstin_masked}</b></div>
                  <div><span className="text-[#8A8A82]">Vinci PAN → Zoho stores</span>{" "}
                    <b className="text-[#2FB999]">{status.vinci_pan_masked || "—"}</b></div>
                  <div><span className="text-[#8A8A82]">Place of supply</span>{" "}
                    <b className="text-[#F5F5F0]">{status.vinci_state_name} · {status.vinci_state_code}</b></div>
                  {status.default_tax_name && (
                    <div><span className="text-[#8A8A82]">Default tax</span>{" "}
                      <b className="text-[#F5F5F0]">{status.default_tax_name}</b></div>
                  )}
                </div>
              </div>
            )}
            {!status.configured && (
              <div className="text-[12px] text-[#C0392B] font-body mt-2">
                ZOHO_BOOKS_CLIENT_ID / SECRET / ORGANIZATION_ID missing in backend/.env
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {status.connected ? (
              <Button data-testid="zoho-books-disconnect" variant="outline"
                       onClick={disconnect} disabled={!!busy}
                       className="border-[#C0392B]/50 text-[#C0392B] hover:bg-[#C0392B]/10 font-grotesk uppercase text-[10px] tracking-[0.22em]">
                <Link2Off size={11} className="mr-1" /> Disconnect
              </Button>
            ) : (
              <Button data-testid="zoho-books-connect" onClick={connect}
                       disabled={!status.configured || !!busy}
                       className="bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk uppercase tracking-[0.22em] text-[11px]">
                {busy === "connect" ? <><Loader2 className="animate-spin mr-1" size={12} /> Redirecting…</>
                                     : <><LinkIcon size={12} className="mr-1" /> Connect Zoho Books</>}
              </Button>
            )}
          </div>
        </div>

        {/* Fallback self-client code paste — visible only when disconnected */}
        {!status.connected && status.configured && (
          <div className="mt-5 pt-5 border-t border-white/10">
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#8A6A1A]">
              Or paste a self-client authorization code
            </Label>
            <div className="flex gap-2 mt-2">
              <Input data-testid="zoho-books-paste-code" value={pasteCode}
                      onChange={(e) => setPasteCode(e.target.value)}
                      placeholder="1000.xxxxxxxxxxxxxxxxxxxxxx"
                      className="bg-black/40 border-white/20 font-mono text-[12px]" />
              <Button data-testid="zoho-books-exchange"
                       onClick={exchange}
                       disabled={!pasteCode || busy === "exchange"}
                       className="bg-[#D8B23E] text-black font-grotesk text-[10px] uppercase tracking-[0.22em]">
                {busy === "exchange" ? <Loader2 className="animate-spin" size={12} /> : <><ClipboardPaste size={11} className="mr-1" /> Exchange</>}
              </Button>
            </div>
            <div className="text-[10px] text-[#6f6f68] mt-2 font-body">
              Codes expire in 3 minutes. Get one from <a href="https://api-console.zoho.in/" target="_blank" rel="noreferrer" className="text-[#D8B23E] underline">api-console.zoho.in</a>.
            </div>
          </div>
        )}
      </div>

      {/* ── PII lock actions ── */}
      {status.connected && (
        <div className="border border-[#D8B23E]/30 rounded-sm p-5 bg-[#1B1401]/40 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">
              Mask Vinci PII in Zoho org profile
            </div>
            <div className="text-[13px] text-[#C9C9C2] font-body mt-1 max-w-2xl">
              Overwrites the Vinci GSTIN + PAN stored at the Zoho org level
              with the masked forms — closes the final leakage point.
              Idempotent · safe to click any number of times.
            </div>
          </div>
          <Button data-testid="zoho-books-mask-org"
                   onClick={async () => {
                     if (!window.confirm("Overwrite the Zoho org profile GSTIN + PAN with masked values?")) return;
                     setBusy("mask");
                     try {
                       const { data } = await api.post("/admin/zoho-books/mask-org-profile", {});
                       toast.success(`Org profile masked · GSTIN ${data.after.gst_no} · PAN ${data.after.pan_no}`);
                       await load();
                     } catch (e) {
                       toast.error(e.response?.data?.detail || "Mask failed");
                     } finally { setBusy(""); }
                   }}
                   disabled={busy === "mask"}
                   className="bg-[#D8B23E] hover:brightness-110 text-black font-grotesk uppercase tracking-[0.22em] text-[10px]">
            {busy === "mask" ? <Loader2 className="animate-spin mr-1" size={12} /> : <><ShieldCheck size={12} className="mr-1" /> Mask Org Profile</>}
          </Button>
        </div>
      )}

      {/* ── Nightly Reconciliation ── */}
      {status.connected && (
        <ReconciliationTile api={api} />
      )}

      {/* ── Backfill tile ── */}
      {status.connected && (
        <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a] flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">
              One-shot backfill
            </div>
            <div className="text-[13px] text-[#C9C9C2] font-body mt-1">
              Sync the last N invoices that don't yet have a Zoho Books ID.
              Recorded payments will book too.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={1} max={200}
                    data-testid="zoho-books-backfill-n"
                    value={backfillN}
                    onChange={(e) => setBackfillN(Number(e.target.value))}
                    className="w-24 bg-black/40 border-white/20 font-mono text-center" />
            <Button data-testid="zoho-books-backfill" onClick={backfill}
                     disabled={busy === "backfill"}
                     className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk uppercase tracking-[0.22em] text-[10px]">
              {busy === "backfill" ? <Loader2 className="animate-spin mr-1" size={12} /> : <><Zap size={12} className="mr-1" /> Backfill</>}
            </Button>
          </div>
        </div>
      )}

      {/* ── Sync log ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="overline text-[#B87333]">Sync log</div>
            <h3 className="font-serif text-2xl mt-1">Recent syncs</h3>
          </div>
          <div className="flex items-center gap-1">
            {["all", "invoice", "payment", "expense"].map((k) => (
              <button key={k} data-testid={`zoho-books-log-filter-${k}`}
                      onClick={() => setLogKind(k)}
                      className={`px-3 py-1.5 text-[10px] font-grotesk uppercase tracking-[0.18em] rounded-sm border ${
                        logKind === k ? "border-[#D8B23E] text-[#D8B23E]"
                                        : "border-white/10 text-[#8A8A82] hover:text-[#C9C9C2]"
                      }`}>
                {k}
                {logCounts[k] && ` (${(logCounts[k].ok || 0) + (logCounts[k].error || 0)})`}
              </button>
            ))}
            <button onClick={load}
                     className="ml-2 text-[#8A8A82] hover:text-[#D8B23E] p-2">
              <RefreshCw size={13} />
            </button>
          </div>
        </div>

        <div className="border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-white/[0.03] text-[#8A8A82]">
              <tr>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">When</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Kind</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Vinci ref</th>
                <th className="text-center px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Status</th>
                <th className="text-left px-4 py-2.5 font-grotesk text-[9px] uppercase tracking-[0.22em]">Detail</th>
              </tr>
            </thead>
            <tbody>
              {logRows.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-[#6f6f68] italic font-body">
                  No sync events yet — connect Zoho Books and issue an invoice.
                </td></tr>
              ) : logRows.map((r, i) => (
                <tr key={i} data-testid={`zoho-log-${i}`}
                    className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[11px] text-[#8A8A82]">{iso(r.at)}</td>
                  <td className="px-4 py-3 text-[#C9C9C2] font-grotesk text-[10px] uppercase tracking-[0.18em]">{r.kind}</td>
                  <td className="px-4 py-3 font-mono text-[#F5F5F0] text-[11px]">{r.vinci_ref}</td>
                  <td className="px-4 py-3 text-center">
                    {r.status === "ok"
                      ? <CheckCircle2 size={13} className="text-[#2FB999] inline" />
                      : <XCircle size={13} className="text-[#C0392B] inline" />}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#C9C9C2] font-body max-w-md truncate" title={r.detail}>
                    {r.detail || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════
   Reconciliation tile · runs the diff on demand + sends digest
   ═════════════════════════════════════════════════════════════════ */
function ReconciliationTile({ api }) {
  const [rep, setRep] = useState(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState("");
  const inr = (n) => `₹${Number(Math.round(n) || 0).toLocaleString("en-IN")}`;

  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.get(`/admin/zoho-books/reconciliation?days=${days}`);
      setRep(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reconciliation failed");
    } finally { setBusy(""); }
  };

  const sendDigest = async () => {
    setBusy("send");
    try {
      await api.post(`/admin/zoho-books/reconciliation/send-digest?days=${days}`, {});
      toast.success("Reconciliation digest sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest send failed");
    } finally { setBusy(""); }
  };

  const t = rep?.totals;
  return (
    <div className="border border-white/10 rounded-sm p-5 bg-[#0a0a0a]"
          data-testid="zoho-books-reconciliation">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333] inline-flex items-center gap-1.5">
            <ClipboardCheck size={11} /> Nightly Reconciliation · 09:00 IST cron
          </div>
          <div className="text-[13px] text-[#C9C9C2] font-body mt-1 max-w-2xl">
            Compares paid Vinci invoices with Zoho Books. Flags missing
            syncs, missing payments and amount mismatches. Runs
            automatically nightly + can be triggered on demand here.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" min={1} max={90}
                  data-testid="zoho-recon-days" value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="w-20 bg-black/40 border-white/20 font-mono text-center" />
          <span className="text-[10px] text-[#8A8A82] uppercase font-grotesk tracking-[0.2em]">days</span>
          <Button data-testid="zoho-recon-run" onClick={run} disabled={busy === "run"}
                   variant="outline"
                   className="border-white/20 text-[#C9C9C2] font-grotesk text-[10px] uppercase tracking-[0.22em]">
            {busy === "run" ? <Loader2 className="animate-spin mr-1" size={12} /> : <><RefreshCw size={11} className="mr-1" /> Run Now</>}
          </Button>
          <Button data-testid="zoho-recon-send" onClick={sendDigest} disabled={busy === "send"}
                   className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.22em]">
            {busy === "send" ? <Loader2 className="animate-spin mr-1" size={12} /> : <><Send size={11} className="mr-1" /> Email Digest</>}
          </Button>
        </div>
      </div>

      {t && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <ReconTile label="Paid · missing in Zoho"
                      count={t.paid_missing_count}
                      amount={t.paid_missing_amount}
                      colour={t.paid_missing_count ? "#C0392B" : "#2FB999"}
                      inr={inr} />
          <ReconTile label="Synced · payment missing"
                      count={t.synced_no_payment_count}
                      amount={t.synced_no_payment_amount}
                      colour={t.synced_no_payment_count ? "#D8B23E" : "#2FB999"}
                      inr={inr} />
          <ReconTile label="Amount mismatches"
                      count={t.mismatch_count}
                      amount={t.mismatch_absolute_delta}
                      colour={t.mismatch_count ? "#C0392B" : "#2FB999"}
                      inr={inr} deltaLabel />
        </div>
      )}

      {rep && !t?.all_clean && (
        <div className="mt-4 space-y-4">
          {rep.paid_in_vinci_not_in_zoho.length > 0 && (
            <ReconTable title="Paid in Vinci · missing in Zoho"
                          rows={rep.paid_in_vinci_not_in_zoho}
                          cols={["invoice_number", "buyer_email", "total", "paid_at"]} />
          )}
          {rep.synced_but_no_payment_in_zoho.length > 0 && (
            <ReconTable title="Synced · payment not recorded"
                          rows={rep.synced_but_no_payment_in_zoho}
                          cols={["invoice_number", "buyer_email", "total", "zoho_books_id"]} />
          )}
          {rep.amount_mismatch.length > 0 && (
            <ReconTable title="Amount mismatches"
                          rows={rep.amount_mismatch}
                          cols={["invoice_number", "vinci_total", "zoho_total", "delta"]} />
          )}
        </div>
      )}
      {t?.all_clean && (
        <div className="mt-4 border border-[#2FB999]/30 bg-[#02150F] rounded-sm p-3 text-center text-[13px] text-[#2FB999] font-body inline-flex items-center gap-2">
          <CheckCircle2 size={13} /> Books are clean · no drift in the last {t.window_days} days
        </div>
      )}
    </div>
  );
}

function ReconTile({ label, count, amount, colour, inr, deltaLabel }) {
  return (
    <div className="border border-white/10 rounded-sm p-3 bg-[#0F0C05]">
      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A6A1A]">{label}</div>
      <div className="font-mono text-xl mt-1" style={{ color: colour }}>
        {count}
        {count > 0 && (
          <span className="text-[12px] ml-2 text-[#8A8A82]">
            {deltaLabel ? "· Δ " : "· "}{inr(amount)}
          </span>
        )}
      </div>
    </div>
  );
}

function ReconTable({ title, rows, cols }) {
  return (
    <div>
      <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E] mb-1.5 inline-flex items-center gap-1.5">
        <AlertTriangle size={10} /> {title} ({rows.length})
      </div>
      <div className="border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-white/[0.03] text-[#8A8A82]">
            <tr>{cols.map((c) => (
              <th key={c} className="text-left px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.2em]">{c.replace(/_/g, " ")}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((r, i) => (
              <tr key={i} className="border-t border-white/5 hover:bg-white/[0.02]">
                {cols.map((c) => (
                  <td key={c} className="px-3 py-1.5 text-[#C9C9C2]">
                    {typeof r[c] === "number"
                      ? `₹${Number(Math.round(r[c])).toLocaleString("en-IN")}`
                      : (r[c] || "—")}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length > 20 && (
              <tr><td colSpan={cols.length} className="px-3 py-2 text-center italic text-[#6f6f68]">
                + {rows.length - 20} more…
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
